import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../../api/document';
import { Tag } from '../../../../../../api/model/tag';
import { MedtechUserV2 } from '../../../../../../api/user';
import { ChangeDocumentOwnerModal } from '../../../../ChangeDocumentOwnerModal/ChangeDocumentOwnerModal';
import { ButtonGroupContext } from '../../../../Contexts/ButtonGroupContext';
import { DocumentOverviewContext } from '../../../Context';
import {
  canUserAccessAtLeastOnePrivateTag,
  getDocumentTagIdsToGroupIdsMap,
} from '../../../DocumentProperties/utils';
import { shouldShowChangeOwnerButton } from '../../../RenderLogic';
import { useFeatureFlagValue } from '../../../../../../hooks/useFeatureFlagValue';

type ChangeOwnerButtonProps = {
  renderClickableItem: (onClick: () => void) => JSX.Element;
  users: MedtechUserV2[];
  tags: Tag[];
};

export const ChangeOwnerButton = ({
  renderClickableItem,
  users,
  tags,
}: ChangeOwnerButtonProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { eventCollaborators } = useFlags();
  const isNewTemplateUpgradeEnabled = useFeatureFlagValue(
    'new-template-upgrade',
  );
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const { setDisabled } = useContext(ButtonGroupContext);
  const { showToast } = useToastProvider();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const commonToastProps = {
    id: 'doc-overview-change-owner-toast',
    replace: true,
  };

  const canChangeOwner = useMemo(
    () =>
      shouldShowChangeOwnerButton(
        qualioDocument,
        currentUser,
        Boolean(eventCollaborators),
        isNewTemplateUpgradeEnabled,
      ),
    [
      qualioDocument,
      currentUser,
      eventCollaborators,
      isNewTemplateUpgradeEnabled,
    ],
  );

  const handleChangeOwner = (ownerId: number) => {
    return documentApi.update({ owner_id: ownerId }, qualioDocument.id);
  };

  const onSuccess = () => {
    showToast({
      'data-cy': 'change-owner-button-notify-success-toast',
      ...commonToastProps,
      title: t('notifications.ownerChanged'),
      status: 'success',
      description: t('success.successfullyChangedDocumentOwner'),
    });
    setDisabled(false);
    refetchDocumentOverviewData(false);
    setIsModalOpen(false);
  };

  const onError = () => {
    showToast({
      'data-cy': 'change-owner-button-notify-error-toast',
      ...commonToastProps,
      title: t('notifications.error'),
      status: 'error',
      description: t('documentActions.failedToChangeOwner'),
    });
    setDisabled(false);
  };

  const documentTagIdsToGroupIdsMap = useMemo(
    () => getDocumentTagIdsToGroupIdsMap(qualioDocument, tags),
    [qualioDocument, tags],
  );

  const potentialOwners = useMemo(
    () =>
      users.filter(
        (user) =>
          documentTagIdsToGroupIdsMap.size === 0 ||
          canUserAccessAtLeastOnePrivateTag(
            user,
            Array.from(documentTagIdsToGroupIdsMap.values()),
          ),
      ),
    [documentTagIdsToGroupIdsMap, users],
  );

  if (!canChangeOwner) {
    return null;
  }

  return (
    <>
      {renderClickableItem(() => {
        setDisabled(true);
        setIsModalOpen(true);
      })}
      {isModalOpen && (
        <ChangeDocumentOwnerModal
          isModalOpen={true}
          modalDescription={t('modals.changeOwnerForDocument')}
          users={potentialOwners}
          onClose={() => setDisabled(false, setIsModalOpen)}
          handleChangeOwner={handleChangeOwner}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
    </>
  );
};
